import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import BadgeEmbed from "../components/BadgeEmbed";
import { API } from "../lib/api";

// Public "get your badge" hub. No login — anyone can find a listed tool and
// copy its embed code. Founders rally their users to upvote → traffic flows
// into the index (the Product-Hunt growth loop).
export default function Badge() {
  const [tools, setTools] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.resolve().then(() => {
      axios
        .get(`${API}/tools`)
        .then((r) => {
          if (!alive) return;
          setTools(r.data?.tools || []);
        })
        .catch(() => {});
    });
    return () => {
      alive = false;
    };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = q
    ? tools
        .filter(
          (t) =>
            (t.title || "").toLowerCase().includes(q) ||
            (t.slug || "").toLowerCase().includes(q),
        )
        .slice(0, 8)
    : [];

  return (
    <div data-testid="badge-page">
      <Seo
        title="Get your Featured badge"
        description="Embed a live TerminalValue.io badge on your site — it shows your real-time upvote count and links back to your profile in the investing tools index."
        path="/badge"
      />
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-xl font-bold border-b border-black pb-2" data-testid="badge-title">
          {">"}_ get your badge
        </h1>

        <p className="mt-4 text-[14px] text-[#222] leading-relaxed">
          Is your tool in the index? Embed the{" "}
          <strong>Featured on TerminalValue.io</strong> badge on your site. It
          renders your <strong>live upvote count</strong> and links back to your
          profile — the more votes you gather, the higher you climb the homepage
          ranking. No account needed. Just find your tool and copy the code.
        </p>

        {/* Search */}
        <div className="mt-6">
          <label className="text-[11px] uppercase tracking-wide text-[#888]" htmlFor="badge-search">
            find your tool
          </label>
          <input
            id="badge-search"
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(null);
            }}
            placeholder="type a tool name…"
            autoComplete="off"
            className="mt-1 w-full border border-black px-3 py-2 text-[14px] outline-none focus:bg-[#FBF8EE]"
            data-testid="badge-search-input"
          />

          {!selected && matches.length > 0 && (
            <ul className="list-none p-0 m-0 mt-2 border border-[#ddd]" data-testid="badge-search-results">
              {matches.map((t) => (
                <li key={t.id} className="border-b border-[#eee] last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setSelected(t)}
                    className="w-full text-left px-3 py-2 text-[14px] hover:bg-[#FBF8EE]"
                    data-testid={`badge-result-${t.slug}`}
                  >
                    {t.title}{" "}
                    <span className="text-[12px] text-[#888]">/tool/{t.slug}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!selected && q && matches.length === 0 && (
            <p className="mt-2 text-[13px] text-[#666]" data-testid="badge-no-match">
              No tool matches “{query}”. Not listed yet?{" "}
              <Link to="/submit" className="tv-link">submit your tool →</Link>
            </p>
          )}
        </div>

        {/* Selected tool → embed code */}
        {selected && (
          <div className="mt-8 border-2 border-[#ff6600] p-4" data-testid="badge-selected">
            <div className="text-[13px] font-bold" data-testid="badge-selected-title">
              {selected.title} — your badge
            </div>
            <p className="text-[12px] text-[#666] mt-1 m-0">
              Paste this anywhere on your site or README.
            </p>
            <div className="mt-3">
              <BadgeEmbed slug={selected.slug} upvotes={selected.upvotes || 0} />
            </div>
            <p className="text-[11px] text-[#888] mt-3 m-0">
              Wrong tool?{" "}
              <button
                type="button"
                className="tv-link"
                onClick={() => setSelected(null)}
                data-testid="badge-clear-selection"
              >
                search again
              </button>
              {" · "}
              Need an update?{" "}
              <Link to="/contact" className="tv-link">contact us</Link>
            </p>
          </div>
        )}

        <p className="mt-10 text-[12px] text-[#888]">
          <Link to="/" className="tv-link">[← back to index]</Link>
        </p>
      </main>
      <Footer />
    </div>
  );
}
